import { useCallback, useEffect, useId, useState, type ReactNode } from "react";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { Combobox, ComboboxMulti } from "./ui/combobox";
import { Switch } from "./ui/switch";
import { UploadScopeSwitches } from "./pricing/upload-scope-switches";
import { useLanguage } from "../i18n/language-provider";
import { fetchRatePlans, fetchRoomTypes, type RatePlanOption } from "../services/pricingApi";
import { fetchConnectionRatePlans, fetchConnectionRoomTypes } from "../services/pmsConnectionsApi";

export interface RoomKindOption {
    id: string;
    label: string;
}

interface PmsSendFiltersProps {
    /**
     * The connection whose room types and rate plans to offer. The auto-sync
     * dialog configures one named property per card on the Connections page,
     * and what it offers is saved onto that property's schedule — so it names
     * its connection. Omitted, the lists are the active connection's, which is
     * right for the send dialog: it sends the active property's drafts.
     *
     * Read when the lists load, and they load once per mount.
     */
    connectionId?: number;
    /** Room kind options to display in the multi-select.
     *  When empty or omitted, fetches room types from the API automatically. */
    roomKindOptions?: RoomKindOption[];
    /** Currently selected room kind IDs (empty = all). */
    selectedRoomKinds: Set<string>;
    onToggleRoomKind: (id: string) => void;
    onClearRoomKinds: () => void;
    /** Currently selected rate plan ID (empty string = base/default). */
    selectedPlanId: string;
    onPlanChange: (id: string) => void;
    /**
     * Told, in place of `onPlanChange`, when the lists load with no plan
     * selected and the base plan is filled in: a pick nobody made. The
     * auto-sync dialog asks before it closes on unsaved changes, and reads
     * this as its form's own default, not as an edit. Omitted, the default
     * goes to `onPlanChange` like any other pick.
     *
     * Keep it stable between renders, as `onPlanChange` has to be: the lists
     * are loaded again whenever either changes.
     */
    onPlanDefaulted?: (id: string) => void;
    /** Show the date range picker (used in send dialogs, hidden in auto-sync). */
    showDateRange?: boolean;
    /** Date range value (e.g. "all", "7"). Only relevant when showDateRange is true. */
    dateRange?: string;
    onDateRangeChange?: (value: string) => void;
    /** Said under the range picker — why the range shown is not the one the dialog opens on. */
    dateRangeHint?: ReactNode;
    /** If true, load data on mount automatically. */
    loadOnMount?: boolean;
    /** Show the dry-run toggle (used in send dialogs). */
    showDryRun?: boolean;
    /** Dry-run toggle state. */
    dryRun?: boolean;
    onDryRunChange?: (value: boolean) => void;
    /**
     * Show the upload-scope switches. Prices and minimum stay are independent
     * layers of an upload — either can be sent without the other — so they are
     * two switches rather than one three-way choice.
     */
    showSendScope?: boolean;
    sendPrices?: boolean;
    onSendPricesChange?: (value: boolean) => void;
    sendMinLos?: boolean;
    onSendMinLosChange?: (value: boolean) => void;
    /**
     * Whether the connected PMS can receive minimum-stay restrictions at all.
     * Defaults to true so a capability that has not loaded yet does not flicker
     * the switch disabled; the backend rejects an unsupported request anyway.
     */
    minLosSupported?: boolean;
    /** Said under the minimum-stay switch while it is on (`MinStayPerNightNotice`). */
    minLosNotice?: ReactNode;
    /**
     * Show the "send again what was already sent" switch (the send dialog):
     * the one way to put back what was changed in the PMS itself, which Optivo
     * cannot see.
     */
    showResend?: boolean;
    resend?: boolean;
    onResendChange?: (value: boolean) => void;
}

export function PmsSendFilters({
    connectionId,
    roomKindOptions: externalRoomKindOptions,
    selectedRoomKinds,
    onToggleRoomKind,
    onClearRoomKinds,
    selectedPlanId,
    onPlanChange,
    onPlanDefaulted,
    showDateRange = false,
    dateRange = "all",
    onDateRangeChange,
    dateRangeHint,
    loadOnMount = false,
    showDryRun = false,
    dryRun = false,
    onDryRunChange,
    showSendScope = false,
    sendPrices = true,
    onSendPricesChange,
    sendMinLos = false,
    onSendMinLosChange,
    minLosSupported = true,
    minLosNotice,
    showResend = false,
    resend = false,
    onResendChange,
}: PmsSendFiltersProps) {
    const { t } = useLanguage();
    const dateRangeId = useId();

    const [ratePlans, setRatePlans] = useState<RatePlanOption[]>([]);
    const [ratePlansLoading, setRatePlansLoading] = useState(false);
    const [ratePlansError, setRatePlansError] = useState(false);

    // Auto-fetched room kinds (used when externalRoomKindOptions is empty)
    const [fetchedRoomKinds, setFetchedRoomKinds] = useState<RoomKindOption[]>([]);

    const needsRoomKindHydration = !!externalRoomKindOptions?.some((option) => {
        const label = option.label.trim();
        return label.length === 0 || label === option.id;
    });

    const roomKindNameById = new Map(fetchedRoomKinds.map((option) => [option.id, option.label]));

    const roomKindOptions = externalRoomKindOptions && externalRoomKindOptions.length > 0
        ? externalRoomKindOptions.map((option) => {
            const hydratedLabel = roomKindNameById.get(option.id);
            const label = option.label.trim();
            if (hydratedLabel && (label.length === 0 || label === option.id)) {
                return { ...option, label: hydratedLabel };
            }
            return option;
        })
        : fetchedRoomKinds;

    const shouldFetchRoomTypes = !externalRoomKindOptions
        || externalRoomKindOptions.length === 0
        || needsRoomKindHydration;

    const loadRatePlans = useCallback(async () => {
        if (ratePlans.length > 0) return;
        setRatePlansLoading(true);
        setRatePlansError(false);
        try {
            const data = connectionId === undefined
                ? await fetchRatePlans()
                : await fetchConnectionRatePlans(connectionId);
            setRatePlans(data.plans);
            // Auto-select the base plan if nothing is selected yet
            if (!selectedPlanId && data.basePlan) {
                (onPlanDefaulted ?? onPlanChange)(String(data.basePlan.ratePlanId));
            }
        } catch {
            setRatePlansError(true);
        } finally {
            setRatePlansLoading(false);
        }
    }, [connectionId, onPlanChange, onPlanDefaulted, ratePlans.length, selectedPlanId]);

    const loadRoomTypes = useCallback(async () => {
        if (fetchedRoomKinds.length > 0) return;
        try {
            const types = connectionId === undefined
                ? await fetchRoomTypes()
                : await fetchConnectionRoomTypes(connectionId);
            setFetchedRoomKinds(
                types.map((rt) => ({ id: String(rt.roomKindId), label: rt.roomKindName })),
            );
        } catch {
            // Non-critical: room kind filter simply won't appear
        }
    }, [connectionId, fetchedRoomKinds.length]);

    useEffect(() => {
        void loadRatePlans();
        if (shouldFetchRoomTypes) {
            void loadRoomTypes();
        }
    }, [loadRatePlans, loadRoomTypes, shouldFetchRoomTypes]);

    // Re-fetch when loadOnMount changes (e.g. dialog opens)
    useEffect(() => {
        if (loadOnMount) {
            void loadRatePlans();
            if (shouldFetchRoomTypes) {
                void loadRoomTypes();
            }
        }
    }, [loadOnMount, loadRatePlans, loadRoomTypes, shouldFetchRoomTypes]);

    return (
        <div className="space-y-3">
            {/* Date range selector */}
            {showDateRange && (
                <div>
                    {/* Bound to the trigger, so the picker is read out by its
                        label rather than as a nameless list. */}
                    <label htmlFor={dateRangeId} className="text-sm font-medium mb-1.5 block">
                        {t.drafts.sendRangeLabel}
                    </label>
                    <Select value={dateRange} onValueChange={onDateRangeChange}>
                        {/* A phone leaves this trigger narrower than its
                            longest label. The select primitive ends the value
                            in an ellipsis there, as the two pickers under it
                            end theirs, so the trigger asks for nothing. */}
                        <SelectTrigger id={dateRangeId} className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">{t.drafts.sendRange1}</SelectItem>
                            <SelectItem value="3">{t.drafts.sendRange3}</SelectItem>
                            <SelectItem value="7">{t.drafts.sendRange7}</SelectItem>
                            <SelectItem value="14">{t.drafts.sendRange14}</SelectItem>
                            <SelectItem value="30">{t.drafts.sendRange30}</SelectItem>
                            <SelectItem value="60">{t.drafts.sendRange60}</SelectItem>
                            <SelectItem value="90">{t.drafts.sendRange90}</SelectItem>
                            <SelectItem value="all">{t.drafts.sendRangeAll}</SelectItem>
                        </SelectContent>
                    </Select>
                    {dateRangeHint && (
                        <p className="mt-1.5 text-xs text-muted-foreground">{dateRangeHint}</p>
                    )}
                </div>
            )}

            {/* Room kinds multi-select */}
            {roomKindOptions.length > 0 && (
                <div>
                    <label className="text-sm font-medium mb-1.5 block">{t.drafts.sendRoomKindsLabel}</label>
                    <ComboboxMulti
                        options={roomKindOptions.map((opt) => ({ value: opt.id, label: opt.label }))}
                        value={selectedRoomKinds}
                        onToggle={onToggleRoomKind}
                        onClear={onClearRoomKinds}
                        allLabel={t.drafts.sendRoomKindsAll}
                        searchPlaceholder={t.drafts.sendRoomKindsSearch}
                        className="w-full"
                    />
                </div>
            )}

            {/* Rate plan selector */}
            <div>
                <label className="text-sm font-medium mb-1.5 block">{t.drafts.ratePlan}</label>
                {ratePlansLoading ? (
                    <p className="text-sm text-muted-foreground">{t.drafts.ratePlanLoading}</p>
                ) : ratePlansError ? (
                    <p className="text-sm text-destructive">{t.drafts.ratePlanError}</p>
                ) : (
                    <Combobox
                        value={selectedPlanId}
                        onValueChange={onPlanChange}
                        placeholder={t.drafts.ratePlanDefault}
                        searchPlaceholder={t.drafts.ratePlanDefault}
                        className="w-full"
                        options={ratePlans.map((plan) => ({
                            value: String(plan.ratePlanId),
                            label: `${plan.name}${plan.isBasePlan ? " ★" : ""}`,
                        }))}
                    />
                )}
            </div>

            {showSendScope && onSendPricesChange && onSendMinLosChange && (
                <UploadScopeSwitches
                    prices={sendPrices}
                    onPricesChange={onSendPricesChange}
                    minLos={sendMinLos}
                    onMinLosChange={onSendMinLosChange}
                    minLosAvailable={minLosSupported}
                    minLosNotice={minLosNotice}
                    labels={{
                        pricesLabel: t.drafts.sendPricesLabel,
                        pricesHint: t.drafts.sendPricesHint,
                        minLosLabel: t.drafts.sendMinLosLabel,
                        minLosHint: t.drafts.sendMinLosHint,
                        minLosUnavailable: t.drafts.sendMinLosUnsupported,
                        nothingSelected: t.drafts.sendScopeNothingSelected,
                    }}
                />
            )}

            {showResend && onResendChange && (
                <div className="flex items-start justify-between gap-3 rounded-md border border-dashed border-border p-3">
                    <div className="min-w-0">
                        <p className="text-sm font-medium">{t.drafts.resendLabel}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{t.drafts.resendHint}</p>
                    </div>
                    <Switch
                        checked={resend}
                        onCheckedChange={onResendChange}
                        aria-label={t.drafts.resendLabel}
                    />
                </div>
            )}

            {showDryRun && onDryRunChange && (
                <div className="flex items-start justify-between gap-3 rounded-md border border-dashed border-border p-3">
                    <div className="min-w-0">
                        <p className="text-sm font-medium">{t.drafts.dryRunLabel}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{t.drafts.dryRunHint}</p>
                    </div>
                    <Switch
                        checked={dryRun}
                        onCheckedChange={onDryRunChange}
                        aria-label={t.drafts.dryRunLabel}
                    />
                </div>
            )}
        </div>
    );
}
